import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { Seo } from "@/components/common/Seo";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { BLOG_POSTS, CLUSTER_LABELS } from "@/data/blog";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export default function Blog() {
  return (
    <div data-testid="blog-page">
      <Seo
        title="Dental Blog | Dr. Ankesh Seth, Dentist in Varanasi"
        description="Dental care tips and local guides from Dr. Ankesh Seth — how to choose the best dentist in Varanasi & Paharia, root canals, dental implants, smile makeovers and more."
        path="/blog"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
        ])}
      />
      <PageHero
        eyebrow="Dental Blog"
        titleLines={["Dental tips &", "local guides"]}
        subtitle="Practical advice on choosing the right dentist in Varanasi and Paharia, plus clear guides to root canals, implants, smile makeovers and more."
        crumbs={[{ label: "Blog" }]}
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-28 lg:px-20">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-brand-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      width="640"
                      height="400"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="inline-block w-fit rounded-full bg-brand-blue/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
                      {CLUSTER_LABELS[post.cluster]}
                    </span>
                    <h2 className="mt-3 font-display text-lg font-medium leading-snug text-brand-dark">
                      {post.title}
                    </h2>
                    <p className="mt-2 flex-1 text-sm text-brand-muted">{post.excerpt}</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-brand-muted">
                      <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(post.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readMins} min read</span>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                      Read article
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Have a dental question?" text="Dr. Ankesh Seth and team are here to help. Call or WhatsApp to book your consultation in Paharia, Varanasi." />
    </div>
  );
}
